import {
  Alert,
  Anchor,
  Box,
  Button,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type SignInInput, signInInputSchema } from '@wwaave/shared/zod';
import { useState } from 'react';

import { AuthShell } from '../components/auth-shell/AuthShell';
import { ApiError, useApiForm } from '../hooks/useApiForm';
import { useAuth } from '../lib/auth-context';

const GENERIC_SIGN_IN_FAIL =
  "We couldn't sign you in with that email and password. Try again, or reset your password if you've forgotten it.";

type SignInResult =
  | { user: { role: 'artist' | 'host' | 'admin' } }
  | { mfaRequired: true; mfaToken: string; csrf: string };

function isMfaRequired(
  r: SignInResult,
): r is { mfaRequired: true; mfaToken: string; csrf: string } {
  return 'mfaRequired' in r && r.mfaRequired === true;
}

function SignInScreen() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const search = Route.useSearch();
  // Non-null once /sign-in returns mfaRequired — swaps the form for the code step.
  const [mfaToken, setMfaToken] = useState<string | null>(null);

  const completeSignIn = async () => {
    await refresh();
    // Honour `?redirect=` if it points back into the app (relative path
    // starting with `/`, no scheme/host — defends against open-redirect smuggling).
    const target = search.redirect;
    if (typeof target === 'string' && target.startsWith('/') && !target.startsWith('//')) {
      window.location.assign(target);
      return;
    }
    void navigate({ to: '/dashboard' });
  };

  const { form, submit, submitting, serverError } = useApiForm<SignInInput, SignInResult>({
    endpoint: '/api/auth/sign-in',
    schema: signInInputSchema,
    initialValues: { email: '', password: '', remember_me: false },
    formOptions: { mode: 'uncontrolled' },
    onSuccess: async (resp) => {
      if (isMfaRequired(resp)) {
        setMfaToken(resp.mfaToken);
        return;
      }
      await completeSignIn();
    },
    mapError: (err) => {
      if (err instanceof ApiError && err.status === 401) return GENERIC_SIGN_IN_FAIL;
      return err.message || GENERIC_SIGN_IN_FAIL;
    },
  });

  // Second factor. The CSRF cookie that /sign-in issued lets this session-less
  // POST clear the global double-submit gate (mutateJson spreads it).
  const challenge = useApiForm<{ code: string }, SignInResult>({
    endpoint: '/api/auth/mfa/challenge',
    initialValues: { code: '' },
    transform: (v) => ({ mfaToken, code: v.code }),
    onSuccess: async () => {
      await completeSignIn();
    },
    mapError: (err) =>
      err instanceof ApiError && err.status === 401
        ? 'That code didn’t match. Try again, or use a recovery code.'
        : err.message || 'We couldn’t verify that code.',
  });

  return (
    <AuthShell>
      <Stack gap="xl">
        {mfaToken === null ? (
          <>
            <Box>
              <Text size="xs" tt="uppercase" c="brand.7" fw={600} style={{ letterSpacing: '0.1em' }}>
                Welcome back
              </Text>
              <Title
                order={1}
                mt={6}
                style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.1rem)', lineHeight: 1.15, letterSpacing: '-0.015em' }}
              >
                Sign in.
              </Title>
              <Text c="ink.6" mt={6} style={{ lineHeight: 1.55 }}>
                Pick up where you left off.
              </Text>
            </Box>

            <form onSubmit={submit} noValidate>
              <Stack gap="md">
                <TextInput
                  label="Email"
                  autoComplete="email"
                  type="email"
                  size="md"
                  {...form.getInputProps('email')}
                  key={form.key('email')}
                />

                <PasswordInput
                  label="Password"
                  autoComplete="current-password"
                  size="md"
                  {...form.getInputProps('password')}
                  key={form.key('password')}
                />

                {serverError !== null && (
                  <Alert color="red" variant="light">
                    {serverError}
                  </Alert>
                )}

                <Button
                  type="submit"
                  loading={submitting}
                  fullWidth
                  size="md"
                  variant="gradient"
                  gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                  mt="xs"
                >
                  Sign in
                </Button>

                <Text size="sm" ta="center">
                  <Anchor component={Link} to="/reset-password" c="brand.7">
                    Forgot your password?
                  </Anchor>
                </Text>

                <Text size="sm" ta="center" c="ink.6">
                  New here?{' '}
                  <Anchor component={Link} to="/sign-up" c="brand.7" fw={500}>
                    Create an account
                  </Anchor>
                </Text>
              </Stack>
            </form>
          </>
        ) : (
          <>
            <Box>
              <Text size="xs" tt="uppercase" c="brand.7" fw={600} style={{ letterSpacing: '0.1em' }}>
                Two-factor
              </Text>
              <Title
                order={1}
                mt={6}
                style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.1rem)', lineHeight: 1.15, letterSpacing: '-0.015em' }}
              >
                Enter your code.
              </Title>
              <Text c="ink.6" mt={6} style={{ lineHeight: 1.55 }}>
                Open your authenticator app and enter the 6-digit code. Lost your device? Use one of
                your recovery codes.
              </Text>
            </Box>

            <form onSubmit={challenge.submit} noValidate>
              <Stack gap="md">
                <TextInput
                  label="Authenticator code"
                  placeholder="123 456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  size="md"
                  {...challenge.form.getInputProps('code')}
                  key={challenge.form.key('code')}
                />

                {challenge.serverError !== null && (
                  <Alert color="red" variant="light">
                    {challenge.serverError}
                  </Alert>
                )}

                <Button
                  type="submit"
                  loading={challenge.submitting}
                  fullWidth
                  size="md"
                  variant="gradient"
                  gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                  mt="xs"
                >
                  Verify
                </Button>

                <Text size="sm" ta="center">
                  <Anchor c="ink.6" style={{ cursor: 'pointer' }} onClick={() => setMfaToken(null)}>
                    Back to sign in
                  </Anchor>
                </Text>
              </Stack>
            </form>
          </>
        )}
      </Stack>
    </AuthShell>
  );
}

type SignInSearch = {
  redirect?: string;
};

export const Route = createFileRoute('/sign-in')({
  // The root `beforeLoad` writes `?redirect=<href>` for protected routes
  // anonymous users land on. Validate loosely — accept either a string or
  // missing — and let `completeSignIn` apply the same-origin guard before using.
  validateSearch: (search: Record<string, unknown>): SignInSearch => {
    const r = search.redirect;
    return typeof r === 'string' ? { redirect: r } : {};
  },
  component: SignInScreen,
});
